import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import LoginIcon from '@mui/icons-material/Login';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useState, useSyncExternalStore, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Logo } from '../components/Logo';
import { isLoginLoopExhausted, resetLoginLoop, subscribeLoginLoop } from './loginLoop';
import { isEntraConfigured, loginRequest } from './msalConfig';
import { getRedirectError } from './redirectError';

function CenteredMessage({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      {children}
    </Box>
  );
}

function SignInScreen() {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);
  const failure = getRedirectError();

  const signIn = () => {
    setRedirecting(true);
    void instance.loginRedirect(loginRequest);
  };

  return (
    <CenteredMessage>
      <Container maxWidth={failure ? 'sm' : 'xs'}>
        <Paper sx={{ p: 4 }} elevation={3}>
          <Stack spacing={3} alignItems="center">
            <Logo width={220} height="auto" />
            {failure ? (
              <Alert severity="error" sx={{ width: '100%' }}>
                <AlertTitle>{t('auth.signInRefused')}</AlertTitle>
                {/* Verbatim: the directory's own wording names the setting that is wrong, which is
                    the only thing that makes this fixable without a network trace. */}
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {failure}
                </Typography>
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t('auth.signInIntro')}
              </Typography>
            )}
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={redirecting ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
              disabled={redirecting}
              onClick={signIn}
            >
              {failure ? t('auth.tryAgain') : t('auth.signIn')}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </CenteredMessage>
  );
}

function Working({ label }: { label: string }) {
  return (
    <CenteredMessage>
      <Stack spacing={2} alignItems="center">
        <Logo width={220} height="auto" />
        <CircularProgress />
        <Typography color="text.secondary">{label}</Typography>
      </Stack>
    </CenteredMessage>
  );
}

function SignInLoopScreen() {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);

  const tryAgain = () => {
    setRedirecting(true);
    resetLoginLoop();
    void instance.loginRedirect(loginRequest).catch((error: unknown) => {
      console.error('Sign-in redirect failed.', error);
      setRedirecting(false);
    });
  };

  return (
    <CenteredMessage>
      <Container maxWidth="sm">
        <Paper sx={{ p: 4 }} elevation={3}>
          <Stack spacing={3} alignItems="center">
            <Logo width={220} height="auto" />
            <Alert severity="error" sx={{ width: '100%' }}>
              <AlertTitle>{t('auth.signInLoopTitle')}</AlertTitle>
              <Typography variant="body2">{t('auth.signInLoopBody')}</Typography>
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={redirecting ? <CircularProgress size={18} color="inherit" /> : <LoginIcon />}
                disabled={redirecting}
                onClick={tryAgain}
              >
                {t('auth.tryAgain')}
              </Button>
              <Button fullWidth disabled={redirecting} onClick={() => void instance.logoutRedirect()}>
                {t('actions.signOut')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </CenteredMessage>
  );
}

/** Blocks the app until an Entra account is signed in. */
export function AuthGate({ children }: { children: ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { t } = useTranslation();
  const loopExhausted = useSyncExternalStore(subscribeLoginLoop, isLoginLoopExhausted, isLoginLoopExhausted);

  if (!isEntraConfigured) {
    return (
      <CenteredMessage>
        <Container maxWidth="sm">
          <Stack spacing={3} alignItems="center">
            <Logo width={220} height="auto" />
            <Alert severity="warning">
              <AlertTitle>{t('auth.entraNotConfiguredTitle')}</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <Trans i18nKey="auth.entraNotConfiguredBody" components={{ code: <code /> }} />
              </Typography>
              <Typography variant="body2">{t('auth.entraNotConfiguredHint')}</Typography>
            </Alert>
          </Stack>
        </Container>
      </CenteredMessage>
    );
  }

  if (loopExhausted) return <SignInLoopScreen />;

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return <Working label={t('auth.starting')} />;
  }

  if (!isAuthenticated) {
    return inProgress === InteractionStatus.None ? <SignInScreen /> : <Working label={t('auth.signingIn')} />;
  }

  return <>{children}</>;
}
